import React, { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAppState } from '../../context/AppStateContext';
import {
    HomeIcon,
    BookOpenIcon,
    ChartBarIcon,
    UserIcon,
    ClipboardDocumentCheckIcon,
    DocumentCheckIcon,
    AcademicCapIcon,
    ClipboardDocumentListIcon,
    QuestionMarkCircleIcon,
    DocumentTextIcon
} from '@heroicons/react/24/outline';

const BottomNav = () => {
    const { user, isAuthenticated } = useAuth();
    const { courses } = useAppState();
    const location = useLocation();
    const navigate = useNavigate();
    const { id: courseId } = useParams();
    const [activeTab, setActiveTab] = React.useState('');

    const isCourseContext = location.pathname.includes('/courses/api/') || location.pathname.includes('/teacher/courses/');

    // Listen for tab changes in course views
    React.useEffect(() => {
        const handleTabChange = (e) => {
            if (e.detail) setActiveTab(e.detail);
        };
        window.addEventListener('change-course-tab', handleTabChange);
        return () => window.removeEventListener('change-course-tab', handleTabChange);
    }, []);

    // Also sync activeTab from URL if possible
    React.useEffect(() => {
        if (!isCourseContext) {
            setActiveTab('');
        }
    }, [location.pathname, isCourseContext]);

    // Calculate unattempted tasks count for student
    const unattemptedCount = useMemo(() => {
        if (!user || user.role !== 'student' || !courses) return 0;
        let count = 0;
        courses.forEach(course => {
            const assignmentSubmissions = course.student_submissions?.assignments || course.assignment_submissions || [];
            const quizSubmissions = course.student_submissions?.quizzes || course.quiz_submissions || [];
            (course.assignments || []).forEach(asgn => {
                const submission = assignmentSubmissions.find(s => s.assignment === asgn.id);
                if (!submission) count++;
            });
            (course.quizzes || []).forEach(quiz => {
                const submissions = quizSubmissions.filter(s => s.quiz === quiz.id);
                if (submissions.length === 0) count++;
            });
        });
        return count;
    }, [user, courses]);

    const navItems = useMemo(() => {
        if (!isAuthenticated || !user) return [];

        if (isCourseContext) {
            if (user.role === 'student') {
                return [
                    { id: 'journey', label: 'Journey', icon: BookOpenIcon, action: () => { } },
                    { id: 'assignments', label: 'Assignments', icon: ClipboardDocumentListIcon, action: () => { } },
                    { id: 'quizzes', label: 'Quizzes', icon: QuestionMarkCircleIcon, action: () => { } },
                    { id: 'submissions', label: 'Performance', icon: AcademicCapIcon, action: () => { } },
                ];
            } else if (user.role === 'teacher') {
                return [
                    { id: 'content', label: 'Strands', icon: BookOpenIcon, action: () => { } },
                    { id: 'assignments', label: 'Assignments', icon: ClipboardDocumentListIcon, action: () => { } },
                    { id: 'quizzes', label: 'Quizzes', icon: QuestionMarkCircleIcon, action: () => { } },
                    { id: 'records', label: 'Records', icon: DocumentTextIcon, action: () => { } },
                    { id: 'gradebook', label: 'Grades', icon: AcademicCapIcon, action: () => { } },
                ];
            }
        }

        if (user.role === 'student') {
            return [
                { path: '/dashboard', label: 'Home', icon: HomeIcon },
                { path: '/tasks', label: 'Tasks', icon: ClipboardDocumentListIcon, badge: unattemptedCount > 0 ? unattemptedCount : null },
                { path: '/courses', label: 'Learning', icon: BookOpenIcon },
                { path: '/success-hub', label: 'Success', icon: ChartBarIcon },
            ];
        } else if (user.role === 'teacher') {
            return [
                { path: '/dashboard', label: 'Home', icon: HomeIcon },
                { path: '/teacher/courses', label: 'My Areas', icon: BookOpenIcon },
                { path: '/teacher/submissions', label: 'Submissions', icon: DocumentCheckIcon },
            ];
        }
        return [];
    }, [user, isAuthenticated, isCourseContext, unattemptedCount]);

    const handleNavItemClick = (item) => {
        if (item.path) {
            navigate(item.path);
        } else {
            window.dispatchEvent(new CustomEvent('change-course-tab', { detail: item.id }));
        }
    };

    const isItemActive = (item) => {
        if (item.path) {
            return location.pathname === item.path;
        }
        return activeTab === item.id;
    };

    if (navItems.length === 0) return null;

    return (
        <nav className="md:hidden fixed bottom-4 left-0 right-0 z-50 px-4 pb-safe pointer-events-none">
            <div className="flex justify-around items-center h-20 max-w-lg mx-auto px-2 pointer-events-auto">
                {navItems.map((item) => {
                    const active = isItemActive(item);
                    return (
                        <button
                            key={item.id || item.path}
                            onClick={() => handleNavItemClick(item)}
                            className="flex flex-col items-center justify-center flex-1 h-full relative group transition-all duration-300"
                        >
                            {/* Active Glow Effect */}
                            {active && (
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <div className="w-14 h-14 bg-[#18216D]/10 blur-xl rounded-full"></div>
                                </div>
                            )}
                            
                            <div className={`p-2 rounded-2xl transition-all duration-300 relative z-10 ${active ? 'text-[#18216D] scale-110' : 'text-slate-400 hover:text-slate-600'}`}>
                                <item.icon className={`w-7 h-7 ${active ? 'stroke-[2.5]' : 'stroke-2'}`} />
                            </div>
                            
                            <span className={`text-[9px] font-black uppercase tracking-widest transition-all duration-300 relative z-10 mt-1 ${active ? 'text-[#18216D]' : 'text-slate-400'}`}>
                                {item.label}
                            </span>

                            {/* Badge Display */}
                            {item.badge && (
                                <span className="absolute top-2 right-4 flex h-4 w-4 items-center justify-center rounded-full bg-rose-500 text-[9px] font-bold text-white ring-2 ring-white z-20">
                                    {item.badge > 9 ? '9+' : item.badge}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        </nav>
    );
};

export default BottomNav;
